import { all, call, put, select, take, takeLatest } from 'redux-saga/effects'

import {
    SET_ACTIVE_FLOOR,
    SET_ACTIVE_FLOOR_FROM_DOC_CHUNK_ID,
    setActiveDrawable,
    setActiveDrawableGroup,
    setActiveFloorFromDocChunkId,
} from '../../../actions/drawable'
import { DocumentChunk } from '../../../models/documentChunk'
import { DocumentMapping } from '../../../models/documentMapping'
import { Project } from '../../../models/project'
import { setActiveHighlightId, setActiveHighlightOpeningId, unsetActiveDrawableId } from '../../slices/2D'
import { setHighlightPopupColor } from '../../slices/common'
import { selectDocumentChunks, selectDocumentMappings } from '../../slices/documents'
import { selectActiveGeometryGroupId } from '../../slices/geometry'
import { sceneRendered } from '../../slices/loading'
import { BasicFloorMapping, selectAllPossibleFloors } from '../../slices/mappings'
import { selectProject } from '../../slices/project'
import { NO_AI_CLASSIFICATION_DATA } from '../../utils/constants'
import { resetActiveFlagAction } from '../2D/resetActiveFlag'
import { isUnclassifiedPage } from '../2D/reviewPlan'

export function* handleSetActiveFloorFromDocChunkId(action: ReturnType<typeof setActiveFloorFromDocChunkId>) {
    try {
        yield put(resetActiveFlagAction({}))
        yield put(setActiveHighlightId(null))
        yield put(setActiveHighlightOpeningId(null))
        yield put(setHighlightPopupColor(null))

        const activeDrawableGroupId: number | null = yield select(selectActiveGeometryGroupId)

        if (activeDrawableGroupId) {
            const index = 0

            yield all([
                put(unsetActiveDrawableId()),
                put(setActiveDrawable(null, index)),
                put(setActiveDrawableGroup(null)),
            ])
        }

        // We must select from the new redux store if we are in a new project
        const documentChunks: DocumentChunk[] | null = yield select(selectDocumentChunks)
        const { allPossibleFloorMappings, floorMappings }: ReturnType<typeof selectAllPossibleFloors> = yield select(
            selectAllPossibleFloors
        )
        const project: Project | null = yield select(selectProject)

        if (!documentChunks || !allPossibleFloorMappings || !project) return

        const documentChunkId = typeof action.payload === 'number' ? action.payload : action.payload.documentChunkId
        const onComplete = typeof action.payload === 'object' ? action.payload.onComplete : undefined

        const documentChunk = documentChunks?.find((c) => c.id === parseInt(documentChunkId))

        const mappings: DocumentMapping[] | null = yield select(selectDocumentMappings)

        if (!mappings || !documentChunk) return

        const mapping = mappings.find((m) => m.document_chunk_id === documentChunk!.id)

        if (!mapping) return

        const matchingFloor: BasicFloorMapping | undefined = allPossibleFloorMappings?.find(
            (f) => f.hash === mapping.floor_hash
        )

        if (!matchingFloor) return

        if (project.is_master_set) {
            // Fall back to a non-option chunk that isn't the current option (avoid redirect loops)
            const fallbackChunkId =
                mappings.find(
                    (m) =>
                        !m.is_option &&
                        !isUnclassifiedPage(m) &&
                        documentChunks.some((c) => c.id === m.document_chunk_id)
                )?.document_chunk_id ?? undefined

            if (mapping.is_option) {
                // Options should never be rendered by themselves, so render their base home if possible
                const estimatedBaseHomeId = mapping.additional_data?.estimatedBaseHomeId

                if (estimatedBaseHomeId != null) {
                    const baseHomeMapping = mappings.find((m) => m.id === estimatedBaseHomeId)
                    const baseHomeChunk = baseHomeMapping
                        ? documentChunks.find((c) => c.id === baseHomeMapping.document_chunk_id)
                        : undefined

                    if (baseHomeChunk) {
                        yield put({ type: SET_ACTIVE_FLOOR_FROM_DOC_CHUNK_ID, payload: baseHomeChunk.id })

                        return
                    }
                }

                if (fallbackChunkId == null || fallbackChunkId === documentChunk.id) return

                yield put({ type: SET_ACTIVE_FLOOR_FROM_DOC_CHUNK_ID, payload: fallbackChunkId })

                return
            }

            if (isUnclassifiedPage(mapping)) {
                if (fallbackChunkId == null || fallbackChunkId === documentChunk.id) return

                yield put({ type: SET_ACTIVE_FLOOR_FROM_DOC_CHUNK_ID, payload: fallbackChunkId })

                return
            }
        }

        const ai_classification =
            floorMappings?.find((f) => f.id === mapping.id)?.ai_classification ?? NO_AI_CLASSIFICATION_DATA // we do not run AI on IMUP only at the present

        yield put({
            type: SET_ACTIVE_FLOOR,
            payload: {
                ...matchingFloor,
                id: mapping.id,
                building_id: mapping.building_id,
                project_document: documentChunk.project_document,
                document_chunk: documentChunk,
                additional_data: {},
                coordinates: mapping.coordinates,
                scale_factor: mapping.scale_factor,
                ai_classification: ai_classification,
            },
        })

        // Wait until the markup finishes rendering (image, drawables, and flags drawn)
        if (onComplete && typeof onComplete === 'function') {
            yield take(sceneRendered.type)
            yield call(onComplete)
        }
    } catch (e) {
        yield call(console.error, e)
    }
}

export function* watchForSetActiveFloorFromDocChunkId() {
    yield takeLatest(SET_ACTIVE_FLOOR_FROM_DOC_CHUNK_ID, handleSetActiveFloorFromDocChunkId)
}
